import moment from 'moment';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import styled from 'styled-components';

import * as graphql from '@treams/treams-network';
import { Button, ChevronDownIcon, Popover, Select, UserCheckIcon, tokens } from '@treams/treams-ui';

import useTalentReviewPath from '../hooks/useTalentReviewPath';

const CURRENT = 'current';

const Trigger = styled(Button)`
	&& {
		border-radius: ${tokens.radius.sm}px;
	}
`;

export default function TalentReviewCurrentSelect(props: {
	groupID: string;
	templateID: string;
	localDate: graphql.Scalars['LocalDate']['input'];
	selectedSnapshotID?: string;
}) {
	const { t } = useTranslation();
	const history = useHistory();
	const location = useLocation();
	const path = useTalentReviewPath();
	const today = moment().format('YYYY-MM-DD');
	const [open, setOpen] = useState(false);

	const query = graphql.useTalentReviewSnapshotsQuery({
		variables: { id: props.groupID, isArchived: false },
	});

	const snapshots = useMemo(
		() =>
			(query.data?.group.talentReviewSnapshots.edges ?? [])
				.map(({ node }) => node as graphql.TalentReviewSnapshot)
				.filter((snapshot) => snapshot.template.id === props.templateID)
				.sort((a, b) => {
					const byDate = b.localDate.localeCompare(a.localDate);
					if (byDate !== 0) {
						return byDate;
					}

					return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
				}),
		[props.templateID, query.data],
	);

	if (snapshots.length === 0) {
		return null;
	}

	const selectedSnapshot = snapshots.find((snapshot) => snapshot.id === props.selectedSnapshotID);
	const currentLabel = selectedSnapshot
		? moment(selectedSnapshot.localDate).format('LL')
		: props.localDate === today
			? t('talentReviews.v2.grid.current')
			: moment(props.localDate).format('L');

	const handleChange = (value?: string) => {
		setOpen(false);

		if (!value || value === CURRENT) {
			if (props.selectedSnapshotID || props.localDate !== today) {
				history.push(
					path({
						groupID: props.groupID,
						templateID: props.templateID,
						localDate: today,
						search: location.search,
					}),
				);
			}
			return;
		}

		history.push(path({ snapshotID: value, search: location.search, manage: true }));
	};

	return (
		<Popover.Target
			visible={open}
			onDismiss={() => setOpen(false)}
			contents={
				<Select.Menu
					selectedValue={props.selectedSnapshotID ?? CURRENT}
					options={[
						{ value: CURRENT, title: t('talentReviews.v2.grid.current'), color: 'yellow' },
						...snapshots.map((snapshot) => ({
							value: snapshot.id,
							title: moment(snapshot.localDate).format('LL'),
							icon: <UserCheckIcon />,
						})),
					]}
					searchPlaceholder={t('talentReviews.v2.grid.snapshotSearchPlaceholder')}
					onChange={handleChange}
					onClose={() => setOpen(false)}
					autofocus
				/>
			}
		>
			<Trigger
				type="secondary"
				title={currentLabel}
				rightIcon={<ChevronDownIcon />}
				aria-haspopup="listbox"
				aria-expanded={open}
				onClick={() => setOpen((value) => !value)}
			/>
		</Popover.Target>
	);
}
